import React, { useState, useEffect, useRef } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useAuth } from '../../context/AuthContext';
import { IoChatbubblesOutline, IoCloseOutline, IoSend, IoPersonCircleOutline } from 'react-icons/io5';
import './SupportChatWidget.css';
import kondyHeaderLogo from '../../assets/Kondy white bg.png';
import kondyMessageLogo from '../../assets/Kondy.png';

function SupportChatWidget({ userName }) {
    const { currentUser, userData } = useAuth();
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [inputVal, setInputVal] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    
    // --- Analytics CSAT States ---
    const [userMessagesCount, setUserMessagesCount] = useState(0);
    const [showCsatModal, setShowCsatModal] = useState(false);
    const [detectedTopic, setDetectedTopic] = useState('Otro');
    const [lastUserMessage, setLastUserMessage] = useState('');
    
    // --- Draggable Logic States ---
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const dragInfo = useRef({
        isDragging: false,
        startX: 0,
        startY: 0,
        initialX: 0,
        initialY: 0,
        hasMoved: false
    });

    const messagesEndRef = useRef(null);

    // --- Window Event Listeners for smooth drag ---
    const handlePointerDown = (e) => {
        // Bloqueamos arrastre si está scrolleando dentro del chat
        if (e.target.closest('.support-chat-body')) return;

        e.currentTarget.setPointerCapture(e.pointerId);
        dragInfo.current = {
            isDragging: true,
            startX: e.clientX,
            startY: e.clientY,
            initialX: position.x,
            initialY: position.y,
            hasMoved: false
        };
    };

    const handlePointerMove = (e) => {
        if (!dragInfo.current.isDragging) return;

        const deltaX = e.clientX - dragInfo.current.startX;
        const deltaY = e.clientY - dragInfo.current.startY;

        // Sensibilidad de movimiento para distinguir entre "Click" y "Drag"
        if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
            dragInfo.current.hasMoved = true;
        }

        setPosition({
            x: dragInfo.current.initialX + deltaX,
            y: dragInfo.current.initialY + deltaY
        });
    };

    const handlePointerUp = (e) => {
        if (dragInfo.current.isDragging) {
            dragInfo.current.isDragging = false;
            e.currentTarget.releasePointerCapture(e.pointerId);
        }
    };

    const handleBubbleClick = (e) => {
        // Si el usuario movió la burbuja, prevenimos abrir/cerrar el chat
        if (dragInfo.current.hasMoved) {
            dragInfo.current.hasMoved = false; // Reset 
            return;
        }
        setIsOpen(true);
    };

    useEffect(() => {
        // Initial Greeting
        if (isOpen && messages.length === 0) {
            setMessages([
                { id: 'msg-0', text: `¡Hola ${userName || 'vecino'}! Soy Kondy 🤖, tu asistente virtual. ¿En qué te puedo ayudar hoy sobre el uso de la app?`, sender: 'bot' }
            ]);
        }
    }, [isOpen, messages.length, userName]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, isTyping]);

    const handleSend = async (e) => {
        e.preventDefault();
        if (!inputVal.trim() || isTyping) return;

        const userMsg = { id: `msg-${Date.now()}`, text: inputVal.trim(), sender: 'user' };
        const updatedMessages = [...messages, userMsg];
        
        setMessages(updatedMessages);
        setLastUserMessage(inputVal.trim());
        setUserMessagesCount(prev => prev + 1);
        setInputVal('');
        setIsTyping(true);

        try {
            const functions = getFunctions();
            // Pointing to us-central1 where functions are usually deployed
            const askBot = httpsCallable(functions, 'askSupportBot');
            
            // We strip out internal IDs before sending
            const sanitizedHistory = updatedMessages.map(m => ({ text: m.text, sender: m.sender }));
            
            const response = await askBot({ messages: sanitizedHistory });
            
            if (response.data?.success) {
                setMessages(prev => [...prev, { id: `msg-${Date.now()}-b`, text: response.data.reply, sender: 'bot' }]);
                if (response.data.topic) setDetectedTopic(response.data.topic);
            } else {
                setMessages(prev => [...prev, { id: `msg-${Date.now()}-e`, text: 'Lo siento, no pude procesar tu solicitud adecuadamente.', sender: 'bot' }]);
            }
        } catch (error) {
            console.error("Error from SupportBot:", error);
            setMessages(prev => [...prev, { id: `msg-${Date.now()}-err`, text: 'Ha ocurrido un error de conexión, intenta más tarde.', sender: 'bot' }]);
        } finally {
            setIsTyping(false);
        }
    };

    const handleCloseChat = () => {
        // Enforce CSAT rating minimum 1 user message
        if (userMessagesCount >= 1 && !showCsatModal) {
            setShowCsatModal(true);
        } else {
            setIsOpen(false);
        }
    };

    const submitCsat = async (isResolved) => {
        try {
            await addDoc(collection(db, 'ai_interactions'), {
                timestamp: serverTimestamp(),
                condominioId: userData?.condominioId || 'GLOBAL',
                userId: currentUser?.uid || 'anonymous',
                rol: userData?.rol || 'guest',
                promptLength: lastUserMessage.length,
                topic: detectedTopic,
                resolved: isResolved
            });
        } catch (e) {
            console.error("Error logging CSAT", e);
        }
        setShowCsatModal(false);
        setIsOpen(false);
        setUserMessagesCount(0);
        setDetectedTopic('Otro');
    };

    const csatOverlayStyle = {
        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 100,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        borderRadius: '16px'
    };
    const csatContentStyle = {
        background: 'white', padding: '20px', borderRadius: '12px',
        textAlign: 'center', width: '85%', boxShadow: '0 10px 25px rgba(0,0,0,0.2)'
    };
    const csatBtnStyle = {
        flex: 1, padding: '10px', border: 'none', borderRadius: '8px', 
        cursor: 'pointer', background: '#f8fafc', fontWeight: '500', transition: 'background 0.2s'
    };
    const csatSkipStyle = {
        background: 'none', border: 'none', color: '#94a3b8', fontSize: '0.8rem',
        marginTop: '15px', cursor: 'pointer', textDecoration: 'underline'
    };

    return (
        <div className="support-widget-container">
            {isOpen ? (
                <div className="support-chat-window animate-pop-in">
                    <div className="support-chat-header">
                        <div className="support-chat-title">
                            <div className="support-avatar-wrap" style={{ width: '38px', height: '38px', borderRadius: '50%', overflow: 'hidden', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff' }}>
                                <img src={kondyHeaderLogo} alt="Kondy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <span className="support-bot-name">Kondy</span>
                                <span className="support-bot-status">Siempre en línea</span>
                            </div>
                        </div>
                        <button className="support-close-btn" onClick={handleCloseChat}>
                            <IoCloseOutline size={26} />
                        </button>
                    </div>

                    {showCsatModal && (
                        <div className="csat-modal-overlay animate-pop-in" style={csatOverlayStyle}>
                            <div className="csat-modal-content glass-card" style={csatContentStyle}>
                                <h4 style={{ margin: '0 0 10px 0', fontSize: '1.05rem', color: '#1e293b' }}>¿Resolvimos tu duda?</h4>
                                <p style={{ margin: '0 0 15px 0', fontSize: '0.85rem', color: '#64748b' }}>Tu opinión nos ayuda a mejorar a Kondy.</p>
                                <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                                    <button style={csatBtnStyle} onClick={() => submitCsat(true)}>👍 Sí</button>
                                    <button style={{ ...csatBtnStyle, border: '1px solid #e2e8f0' }} onClick={() => submitCsat(false)}>👎 No</button>
                                </div>
                                <button style={csatSkipStyle} onClick={() => { setShowCsatModal(false); setIsOpen(false); }}>Omitir</button>
                            </div>
                        </div>
                    )}

                    <div className="support-chat-body">
                        {messages.map((m) => (
                            <div key={m.id} className={`support-msg-bubble ${m.sender === 'user' ? 'user' : 'bot'}`}>
                                {m.sender === 'bot' && (
                                    <div className="support-bubble-avatar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', padding: 0 }}>
                                        <img src={kondyMessageLogo} alt="Kondy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    </div>
                                )}
                                <div className="support-bubble-content">
                                    {m.text}
                                </div>
                            </div>
                        ))}
                        {isTyping && (
                            <div className="support-msg-bubble bot typing-indicator-wrap">
                                <div className="support-bubble-avatar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', padding: 0 }}>
                                    <img src={kondyMessageLogo} alt="Kondy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                </div>
                                <div className="typing-indicator">
                                    <span></span><span></span><span></span>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    <form className="support-chat-footer" onSubmit={handleSend}>
                        <input 
                            type="text" 
                            placeholder="Escribe tu duda aquí..." 
                            value={inputVal}
                            onChange={e => setInputVal(e.target.value)}
                            disabled={isTyping}
                        />
                        <button type="submit" disabled={!inputVal.trim() || isTyping}>
                            <IoSend size={20} />
                        </button>
                    </form>
                </div>
            ) : (
                <button 
                    className={`support-floating-btn shadow-lg ${!dragInfo.current.isDragging && position.x === 0 && position.y === 0 ? 'animate-bounce-slow' : ''}`} 
                    onClick={handleBubbleClick}
                    style={{ 
                        transform: `translate(${position.x}px, ${position.y}px)`,
                        transition: dragInfo.current.isDragging ? 'none' : 'transform 0.3s ease, box-shadow 0.3s ease',
                        cursor: dragInfo.current.isDragging ? 'grabbing' : 'pointer'
                    }}
                    onPointerDown={handlePointerDown}
                    onPointerMove={handlePointerMove}
                    onPointerUp={handlePointerUp}
                    onPointerCancel={handlePointerUp}
                >
                    <IoChatbubblesOutline size={30} style={{ pointerEvents: 'none' }} />
                </button>
            )}
        </div>
    );
}

export default SupportChatWidget;
